import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FaPlus, FaMinus } from 'react-icons/fa';
import LandingNavbar from './Navbar';
import LandingFooter from './LandingFooter';
import ScrollReveal from './ScrollReveal';
import { useDemoModal } from './DemoModal';
import appIcon from '../../assets/images/eb/app-icon.png';
import logoPrimary from '../../assets/images/eb/logo-primary.png';
import {
  CO_HERO, CO_STORY, CO_SERVICES, CO_PLATFORM, CO_TEAM, CO_PROCESS,
  CO_AWARDS, CO_TESTIMONIALS, CO_FAQS, CO_CONTACT,
} from './companyContent';
import './landing-animations.css';
import './landing-theme.css';
import './landing-eb.css';
import './landing-eb-company.css';

// Brand icons: src/assets/images/eb/icons/<name>-<green|cream|gold>.png
const iconCtx = require.context('../../assets/images/eb/icons', false, /\.png$/);
const icon = (name, tone = 'green') => iconCtx(`./${name}-${tone}.png`);

const Photo = ({ src, caption, className }) => (
  src
    ? <img className={className} src={src} alt="" />
    : (
      <div className="eb-co-ph" aria-hidden="true">
        <img src={icon('image')} alt="" />
        {caption && <span>{caption}</span>}
      </div>
    )
);

const CompanyPage = () => {
  const location = useLocation();
  const { openDemo: goDemo } = useDemoModal(); // "Book a demo" opens the demo form
  const [openFaq, setOpenFaq] = useState(0);
  const [t, setT] = useState(0);

  useEffect(() => {
    if (!location.hash) return undefined;
    const timer = window.setTimeout(() => {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const total = CO_TESTIMONIALS.items.length;
  const cur = CO_TESTIMONIALS.items[t];
  const prev = () => setT((i) => (i + total - 1) % total);
  const next = () => setT((i) => (i + 1) % total);

  return (
    <div className="landing-page eb min-vh-100 d-flex flex-column">
      <ScrollReveal />
      <LandingNavbar />

      {/* ── Hero ── */}
      <header className="eb-co-hero">
        <div className="eb-co-hero-copy">
          <p className="eb-eyebrow mb-0">{CO_HERO.eyebrow}</p>
          <h1 className="eb-co-h1">{CO_HERO.title}</h1>
          <p className="eb-lead mb-0">{CO_HERO.subtitle}</p>
          <span className="eb-co-tagline">{CO_HERO.tagline}</span>
        </div>
        <div className="eb-co-hero-media">
          <div className="eb-co-hero-photo">
            <Photo src={CO_HERO.photo} caption={CO_HERO.photoCaption} />
          </div>
          <div className="eb-co-stats">
            {CO_HERO.stats.map((s) => (
              <div key={s.k}><b>{s.v}</b><span>{s.k}</span></div>
            ))}
          </div>
        </div>
      </header>

      {/* ── Story ── */}
      <section className="eb-section eb-co-story-sec">
        <Container className="eb-co-two">
          <div className="eb-co-col">
            <p className="eb-eyebrow mb-0">{CO_STORY.eyebrow}</p>
            <h2 className="eb-h2 reveal reveal-up">{CO_STORY.title}</h2>
          </div>
          <div className="eb-co-col">
            <p className="eb-co-body">{CO_STORY.p1}</p>
            <p className="eb-co-body is-muted">{CO_STORY.p2}</p>
          </div>
        </Container>
      </section>

      {/* ── What we do ── */}
      <section className="eb-section eb-section-cream">
        <Container className="eb-co-block">
          <div className="eb-co-center reveal reveal-up">
            <p className="eb-eyebrow mb-0">{CO_SERVICES.eyebrow}</p>
            <h2 className="eb-h2">{CO_SERVICES.title}</h2>
            <p className="eb-lead mb-0">{CO_SERVICES.subtitle}</p>
          </div>
          <div className="eb-co-services">
            {CO_SERVICES.items.map((s) => (
              <div key={s.k} className="eb-co-service reveal reveal-up">
                <img src={icon(s.icon)} alt="" />
                <span>{s.k}</span>
              </div>
            ))}
            <Link to={CO_SERVICES.platformTo} className="eb-co-service is-link">
              {CO_SERVICES.platformLabel}<span aria-hidden="true">→</span>
            </Link>
          </div>
        </Container>
      </section>

      {/* ── EthosBoard platform ── */}
      <section className="eb-section">
        <Container className="eb-co-two is-center">
          <div className="eb-co-brand reveal reveal-up">
            <img src={appIcon} alt="EthosBoard app icon" className="icon" />
            <img src={logoPrimary} alt="EthosBoard" className="logo" />
            <span>{CO_PLATFORM.deploy}</span>
          </div>
          <div className="eb-co-col">
            <p className="eb-eyebrow mb-0">{CO_PLATFORM.eyebrow}</p>
            <h2 className="eb-h2 reveal reveal-up">{CO_PLATFORM.title}</h2>
            <p className="eb-co-body is-muted">{CO_PLATFORM.text}</p>
            <Link to={CO_PLATFORM.linkTo} className="eb-co-link">{CO_PLATFORM.link}</Link>
          </div>
        </Container>
      </section>

      {/* ── Team ── */}
      <section className="eb-section eb-section-cream">
        <Container className="eb-co-two">
          <div className="eb-co-col">
            <p className="eb-eyebrow mb-0">{CO_TEAM.eyebrow}</p>
            <h2 className="eb-h2 reveal reveal-up">{CO_TEAM.title}</h2>
            <p className="eb-co-body is-muted">{CO_TEAM.text}</p>
            <div className="eb-co-leader">
              <div className="eb-co-leader-photo">
                <Photo src={CO_TEAM.leader.photo} caption={CO_TEAM.leader.photoCaption} />
              </div>
              <div className="info">
                <span className="tag">{CO_TEAM.leader.tag}</span>
                <strong>{CO_TEAM.leader.name}</strong>
                <span className="role">{CO_TEAM.leader.role}</span>
                <span className="bio">{CO_TEAM.leader.bio}</span>
              </div>
            </div>
          </div>
          <div className="eb-co-roles">
            {CO_TEAM.roles.map((r) => (
              <div key={r.k} className="eb-co-role">
                <strong>{r.k}</strong>
                <span>{r.d}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── How we work ── */}
      <section className="eb-section">
        <Container className="eb-co-block">
          <div className="eb-co-center reveal reveal-up">
            <p className="eb-eyebrow mb-0">{CO_PROCESS.eyebrow}</p>
            <h2 className="eb-h2">{CO_PROCESS.title}</h2>
            <p className="eb-lead mb-0">{CO_PROCESS.subtitle}</p>
          </div>
          <div className="eb-co-stages">
            {CO_PROCESS.stages.map((s, i) => (
              <div key={s.k} className={`eb-co-stage reveal reveal-up${s.accent ? ' is-accent' : ''}`}>
                <span className="n">0{i + 1}</span>
                <strong>{s.k}</strong>
                <span>{s.d}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Awards and sectors ── */}
      <section className="eb-section eb-section-dark">
        <Container className="eb-co-two">
          <div className="eb-co-col">
            <p className="eb-eyebrow on-dark mb-0">{CO_AWARDS.eyebrow}</p>
            <h2 className="eb-h2 reveal reveal-up">{CO_AWARDS.title}</h2>
            {CO_AWARDS.items.map((a) => (
              <div key={a.k} className="eb-co-award">
                <img src={icon('award', 'gold')} alt="" />
                <span><strong>{a.m}:</strong> {a.k}</span>
              </div>
            ))}
          </div>
          <div className="eb-co-col">
            <p className="eb-eyebrow on-dark mb-0">{CO_AWARDS.sectorsEyebrow}</p>
            <p className="eb-co-body">{CO_AWARDS.sectorsText}</p>
            <div className="eb-co-chips">
              {CO_AWARDS.sectors.map((s) => <span key={s}>{s}</span>)}
            </div>
            <Link to={CO_AWARDS.linkTo} className="eb-co-link is-gold">{CO_AWARDS.link}</Link>
          </div>
        </Container>
      </section>

      {/* ── Testimonials ── */}
      <section className="eb-section">
        <Container className="eb-co-block">
          <div className="eb-co-t-head">
            <div className="eb-co-col">
              <p className="eb-eyebrow mb-0">{CO_TESTIMONIALS.eyebrow}</p>
              <h2 className="eb-h2">{CO_TESTIMONIALS.title}</h2>
            </div>
            <div className="eb-jobs-nav">
              <button type="button" className="eb-arrow" aria-label="Previous testimonial" onClick={prev}>←</button>
              <button type="button" className="eb-arrow is-next" aria-label="Next testimonial" onClick={next}>→</button>
            </div>
          </div>

          <div className="eb-co-t-grid">
            <figure className="eb-co-quote">
              <span className="eb-co-qmark" aria-hidden="true">“</span>
              <blockquote>{cur.quote}</blockquote>
              <figcaption>
                <div className="avatar"><Photo src={cur.photo} /></div>
                <div className="who"><strong>{cur.name}</strong><span>{cur.role}</span></div>
                <div className="logo"><Photo src={cur.logo} /></div>
              </figcaption>
            </figure>
            <div className="eb-co-t-list" role="tablist" aria-label="Choose a testimonial">
              {CO_TESTIMONIALS.items.map((x, i) => (
                <button
                  key={x.headline}
                  type="button"
                  role="tab"
                  aria-selected={t === i}
                  className={`eb-co-t-item${t === i ? ' is-on' : ''}`}
                  onClick={() => setT(i)}
                >
                  <span className="ind">{x.industry}</span>
                  <span className="hd">{x.headline}</span>
                  <span className="by">{x.name} · {x.org}</span>
                </button>
              ))}
            </div>
          </div>
          <p className="eb-co-fine">
            {CO_TESTIMONIALS.linkBefore}<Link to={CO_TESTIMONIALS.linkTo}>{CO_TESTIMONIALS.linkLabel}</Link>.
          </p>
        </Container>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="eb-section">
        <Container style={{ maxWidth: 900 }}>
          <h2 className="eb-h2 text-center mb-4 reveal reveal-up">Frequently asked questions</h2>
          <div className="eb-co-faq-list">
            {CO_FAQS.map(({ q, a }, i) => {
              const open = openFaq === i;
              return (
                <div key={q} className="eb-faq-item">
                  <button
                    type="button"
                    className="eb-faq-q"
                    aria-expanded={open}
                    aria-controls={`eb-co-faq-${i}`}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                  >
                    {q}
                    <span className="eb-faq-sign">{open ? <FaMinus size={12} /> : <FaPlus size={12} />}</span>
                  </button>
                  {open && <div id={`eb-co-faq-${i}`} className="eb-faq-a">{a}</div>}
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ── Contact ── */}
      <section id="demo" className="eb-section pt-0">
        <Container>
          <div className="eb-co-contact reveal reveal-up">
            <div className="eb-co-col">
              <h2 className="eb-h2">{CO_CONTACT.title}</h2>
              <p className="eb-co-body is-muted">{CO_CONTACT.text}</p>
              <div className="d-flex flex-wrap gap-3">
                <button type="button" className="landing-btn-primary" onClick={goDemo}>{CO_CONTACT.demo}</button>
                <a className="landing-btn-secondary eb-co-mail" href={`mailto:${CO_CONTACT.email}`}>{CO_CONTACT.email}</a>
              </div>
            </div>
            <div className="eb-co-offices">
              {CO_CONTACT.offices.map((o) => (
                <div key={o.city} className="eb-co-office">
                  <img src={icon('globe')} alt="" />
                  <div><strong>{o.city}</strong><span>{o.addr}</span></div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <LandingFooter />
    </div>
  );
};

export default CompanyPage;